import React, { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Plus, Trash, UploadSimple, X, Pencil, FloppyDisk, MagnifyingGlass, VideoCamera, Image as ImageIcon, FilmStrip, YoutubeLogo } from "@phosphor-icons/react";
import ExerciseMedia, { detectMediaType, extractYouTubeId } from "../components/ExerciseMedia";

const MEDIA_TYPE_OPTIONS = [
  { key: "image", label: "Imagen", icon: ImageIcon },
  { key: "gif", label: "GIF", icon: FilmStrip },
  { key: "video", label: "Video", icon: VideoCamera },
  { key: "youtube", label: "YouTube", icon: YoutubeLogo },
];

const EMPTY_FORM = {
  name: "",
  category: "",
  description: "",
  image_url: "",
  video_url: "",
  media_type: "image",
  muscles: "",
  equipment: "",
  instructions: "",
  parent_id: "",
  difficulty: "",
};

const DIFFICULTY_OPTIONS = [
  { key: "principiante", label: "Principiante", color: "#32D74B" },
  { key: "intermedio", label: "Intermedio", color: "#FFD60A" },
  { key: "avanzado", label: "Avanzado", color: "#FF3B30" },
];

export default function Exercises() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [exercises, setExercises] = useState([]);
  const [filter, setFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [muscleFilter, setMuscleFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // exercise being edited (null = create mode)
  const [form, setForm] = useState(EMPTY_FORM);
  const [uploading, setUploading] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/exercises");
      setExercises(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  useEffect(() => { load(); }, []);

  const categories = useMemo(() => {
    const s = new Set(exercises.map((e) => e.category).filter(Boolean));
    return ["all", ...Array.from(s)];
  }, [exercises]);

  // Aggregate primary muscles across the catalog.
  // - Case-insensitive dedupe (keeps the first-seen casing for display).
  // - Only looks at parent exercises (same set the grid shows).
  // - Sorted by frequency desc so the most-used muscles appear first.
  const muscles = useMemo(() => {
    const counts = new Map(); // key: normalized, val: {label, count}
    for (const e of exercises) {
      if (e.parent_id) continue;
      if (!Array.isArray(e.muscles)) continue;
      for (const raw of e.muscles) {
        const label = (raw || "").trim();
        if (!label) continue;
        const key = label.toLowerCase();
        const cur = counts.get(key);
        if (cur) cur.count += 1;
        else counts.set(key, { label, count: 1 });
      }
    }
    return Array.from(counts.values()).sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  }, [exercises]);

  const filtered = exercises.filter((e) => {
    // Grid principal: solo ejercicios "parent" (los que no tienen padre)
    if (e.parent_id) return false;
    const matchText = e.name.toLowerCase().includes(filter.toLowerCase()) ||
      (e.category || "").toLowerCase().includes(filter.toLowerCase()) ||
      (Array.isArray(e.muscles) ? e.muscles.join(" ") : "").toLowerCase().includes(filter.toLowerCase());
    const matchCat = categoryFilter === "all" || e.category === categoryFilter;
    const matchMuscle =
      muscleFilter === "all" ||
      (Array.isArray(e.muscles) &&
        e.muscles.some((m) => (m || "").toLowerCase() === muscleFilter));
    return matchText && matchCat && matchMuscle;
  });

  const openCreate = (parentExercise = null) => {
    setEditing(null);
    setForm({
      ...EMPTY_FORM,
      parent_id: parentExercise?.exercise_id || "",
      // Pre-llenamos la categoría del parent para que la variante herede por defecto
      category: parentExercise?.category || "",
      difficulty: parentExercise ? "principiante" : "",
    });
    setShowForm(true);
    // Cerrar modal detalle si estaba abierto para dejar ver el form
    setDetail(null);
  };

  const openEdit = (ex) => {
    setEditing(ex);
    setForm({
      name: ex.name || "",
      category: ex.category || "",
      description: ex.description || "",
      image_url: ex.image_url || "",
      video_url: ex.video_url || "",
      media_type: ex.media_type || detectMediaType(ex.video_url || ex.image_url),
      muscles: Array.isArray(ex.muscles) ? ex.muscles.join(", ") : (ex.muscles || ""),
      equipment: ex.equipment || "",
      instructions: ex.instructions || "",
      parent_id: ex.parent_id || "",
      difficulty: ex.difficulty || "",
    });
    setShowForm(true);
    setDetail(null);
  };

  // Auto-detecta el tipo si el usuario pega una URL externa o sube archivo
  const handleUrlChange = (field, value) => {
    const next = { ...form, [field]: value };
    const detected = detectMediaType(value);
    if (value && detected !== "image") {
      next.media_type = detected;
    }
    setForm(next);
  };

  const handleUpload = async (e, target) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isVideo = file.type.startsWith("video/");
    const isGif = file.type === "image/gif";
    if (file.size > 50 * 1024 * 1024) {
      toast.error("Archivo muy grande (máx 50MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setForm((f) => {
        const next = { ...f };
        if (target === "video" || isVideo) {
          next.video_url = fullUrl;
          next.media_type = "video";
        } else if (isGif) {
          next.image_url = fullUrl;
          next.media_type = "gif";
        } else {
          next.image_url = fullUrl;
          if (next.media_type === "image" || !next.media_type) next.media_type = "image";
        }
        return next;
      });
      toast.success(isVideo ? "Video subido" : isGif ? "GIF subido" : "Imagen subida");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (e.target) e.target.value = "";
    }
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      // Convertimos strings vacíos a null para que el backend los trate opcionales
      parent_id: form.parent_id || null,
      difficulty: form.difficulty || null,
      muscles: form.muscles
        ? form.muscles.split(",").map((s) => s.trim()).filter(Boolean)
        : null,
    };
    // Validación mínima por tipo
    if (payload.media_type === "youtube" && !extractYouTubeId(payload.video_url || "")) {
      toast.error("Pegá una URL válida de YouTube");
      return;
    }
    try {
      if (editing) {
        await api.put(`/exercises/${editing.exercise_id}`, payload);
        toast.success("Ejercicio actualizado");
      } else {
        await api.post("/exercises", payload);
        toast.success("Ejercicio agregado");
      }
      setShowForm(false);
      setEditing(null);
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar ejercicio?")) return;
    try {
      await api.delete(`/exercises/${id}`);
      toast.success("Eliminado");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <Layout>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-6 gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted mb-2">Biblioteca</div>
          <h1 className="font-display font-black text-5xl tracking-tighter uppercase">Ejercicios</h1>
          <div className="text-sm text-muted mt-1">{exercises.length} ejercicios · imágenes, GIFs, videos y YouTube</div>
        </div>
        <div className="flex gap-3 flex-wrap">
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
            <input
              className="input-base pl-9 !w-64"
              placeholder="Buscar por nombre, grupo, músculo..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              data-testid="exercise-filter"
            />
          </div>
          {isAdmin && (
            <button onClick={openCreate} className="btn-primary flex items-center gap-2" data-testid="new-exercise-btn">
              <Plus size={18} weight="bold" />Nuevo
            </button>
          )}
        </div>
      </div>

      {categories.length > 1 && (
        <div className="flex gap-2 flex-wrap mb-3">
          {categories.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCategoryFilter(c)}
              className={`text-[10px] uppercase tracking-widest font-bold border px-3 py-1.5 transition-colors ${
                categoryFilter === c ? "border-[#FF3B30] bg-[#FF3B30] text-white" : "border-[#262626] text-muted hover:text-white"
              }`}
              data-testid={`exercise-cat-${c}`}
            >
              {c === "all" ? "Todas las categorías" : c}
            </button>
          ))}
        </div>
      )}

      {muscles.length > 0 && (
        <div className="mb-6" data-testid="muscle-filter-bar">
          <div className="text-[10px] uppercase tracking-widest text-muted mb-2">
            Músculo primario
            {muscleFilter !== "all" && (
              <button
                type="button"
                onClick={() => setMuscleFilter("all")}
                className="ml-2 text-primary-app hover:underline normal-case tracking-normal"
                data-testid="clear-muscle-filter"
              >
                · limpiar
              </button>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setMuscleFilter("all")}
              className={`text-[10px] uppercase tracking-widest font-bold border px-3 py-1.5 transition-colors ${
                muscleFilter === "all" ? "border-[#007AFF] bg-[#007AFF] text-white" : "border-[#262626] text-muted hover:text-white"
              }`}
              data-testid="muscle-all"
            >
              Todos los músculos
            </button>
            {muscles.map((m) => {
              const key = m.label.toLowerCase();
              const active = muscleFilter === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setMuscleFilter(key)}
                  className={`flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold border px-3 py-1.5 transition-colors ${
                    active ? "border-[#007AFF] bg-[#007AFF] text-white" : "border-[#262626] text-muted hover:text-white"
                  }`}
                  data-testid={`muscle-${key.replace(/\s+/g, "-")}`}
                >
                  {m.label}
                  <span className={`text-[9px] normal-case tracking-normal ${active ? "text-white/70" : "opacity-60"}`}>· {m.count}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {showForm && isAdmin && (
        <ExerciseForm
          form={form}
          setForm={setForm}
          editing={editing}
          uploading={uploading}
          allExercises={exercises}
          onUpload={handleUpload}
          onUrlChange={handleUrlChange}
          onSave={save}
          onCancel={() => { setShowForm(false); setEditing(null); setForm(EMPTY_FORM); }}
        />
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="exercises-grid">
        {filtered.map((ex) => (
          <div
            key={ex.exercise_id}
            className="card-base hover:!border-[#FF3B30] transition-colors group overflow-hidden cursor-pointer"
            onClick={() => setDetail(ex)}
            data-testid={`exercise-card-${ex.exercise_id}`}
          >
            <div className="aspect-square bg-[#0A0A0A] overflow-hidden relative">
              <ExerciseMedia exercise={ex} size="sm" />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent p-3 pointer-events-none">
                {ex.category && (
                  <span className="text-[10px] uppercase tracking-widest text-primary-app font-bold">{ex.category}</span>
                )}
              </div>
              {ex.variant_count > 0 && (
                <div className="absolute top-2 right-2 text-[10px] uppercase tracking-widest font-bold bg-[#FF3B30] text-white px-2 py-0.5 shadow-lg pointer-events-none" data-testid={`variant-badge-${ex.exercise_id}`}>
                  +{ex.variant_count} {ex.variant_count === 1 ? "variante" : "variantes"}
                </div>
              )}
            </div>
            <div className="p-4">
              <h3 className="font-display font-bold uppercase tracking-tight text-lg mb-1 line-clamp-1">{ex.name}</h3>
              {Array.isArray(ex.muscles) && ex.muscles.length > 0 ? (
                <div className="flex flex-wrap gap-1 mb-1">
                  {ex.muscles.slice(0, 3).map((m) => (
                    <span key={m} className="text-[9px] uppercase tracking-widest bg-[#262626] text-muted px-1.5 py-0.5">{m}</span>
                  ))}
                </div>
              ) : ex.description ? (
                <p className="text-xs text-muted line-clamp-2">{ex.description}</p>
              ) : null}
              {isAdmin && (
                <div className="flex gap-3 mt-3 text-xs" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => openEdit(ex)} className="text-muted hover:text-white flex items-center gap-1" data-testid={`edit-exercise-${ex.exercise_id}`}>
                    <Pencil size={12} weight="bold" /> Editar
                  </button>
                  <button onClick={() => handleDelete(ex.exercise_id)} className="text-muted hover:text-[#FF3B30] flex items-center gap-1" data-testid={`delete-exercise-${ex.exercise_id}`}>
                    <Trash size={12} weight="bold" /> Eliminar
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full text-center py-12 text-muted text-sm" data-testid="exercises-empty">
            {exercises.length === 0
              ? "Sin ejercicios aún. Tocá Nuevo para crear el primero."
              : (muscleFilter !== "all" || categoryFilter !== "all" || filter)
                ? "Sin resultados para los filtros actuales. Probá ajustar categoría, músculo o texto."
                : "Sin resultados"}
          </div>
        )}
      </div>

      {detail && (
        <ExerciseDetailModal
          exercise={detail}
          allExercises={exercises}
          isAdmin={isAdmin}
          onClose={() => setDetail(null)}
          onOpenVariant={(v) => setDetail(v)}
          onAddVariant={() => openCreate(detail)}
          onEdit={() => openEdit(detail)}
        />
      )}
    </Layout>
  );
}

function ExerciseForm({ form, setForm, editing, uploading, allExercises = [], onUpload, onUrlChange, onSave, onCancel }) {
  // Candidatos a "parent": todos los ejercicios que NO son variantes y no son el propio editing
  const parentCandidates = allExercises.filter((e) => !e.parent_id && (!editing || e.exercise_id !== editing.exercise_id));
  return (
    <form onSubmit={onSave} className="card-base p-6 mb-8 space-y-5" data-testid="exercise-form">
      <div className="flex items-center justify-between">
        <h2 className="font-display font-black uppercase text-xl">
          {editing ? "Editar ejercicio" : (form.parent_id ? "Nueva variante" : "Nuevo ejercicio")}
        </h2>
        <button type="button" onClick={onCancel} className="text-muted hover:text-white" data-testid="close-exercise-form">
          <X size={20} weight="bold" />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs uppercase tracking-widest text-muted block mb-2">Nombre</label>
          <input className="input-base" placeholder="Sentadilla con barra" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="ex-name" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-muted block mb-2">Categoría</label>
          <input className="input-base" placeholder="Piernas, Torso, Cardio..." value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="ex-category" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-muted block mb-2">Músculos trabajados (coma)</label>
          <input className="input-base" placeholder="cuádriceps, glúteo, core" value={form.muscles} onChange={(e) => setForm({ ...form, muscles: e.target.value })} data-testid="ex-muscles" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-muted block mb-2">Equipamiento</label>
          <input className="input-base" placeholder="barra, mancuernas..." value={form.equipment} onChange={(e) => setForm({ ...form, equipment: e.target.value })} data-testid="ex-equipment" />
        </div>
      </div>

      <div>
        <label className="text-xs uppercase tracking-widest text-muted block mb-2">Descripción corta</label>
        <textarea className="input-base" rows="2" placeholder="Técnica básica, tips clave..." value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="ex-description" />
      </div>

      {/* Variante de otro ejercicio + dificultad */}
      <div className="border-t border-app pt-5">
        <div className="text-xs uppercase tracking-widest text-muted mb-3">Variante (opcional)</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-muted block mb-2">Ejercicio madre</label>
            <select
              className="input-base"
              value={form.parent_id}
              onChange={(e) => setForm({ ...form, parent_id: e.target.value })}
              data-testid="ex-parent-id"
            >
              <option value="">— Sin padre (ejercicio principal) —</option>
              {parentCandidates.map((p) => (
                <option key={p.exercise_id} value={p.exercise_id}>
                  {p.name}{p.category ? ` · ${p.category}` : ""}
                </option>
              ))}
            </select>
            <div className="text-[10px] text-muted mt-1">Si lo linkeás a un padre, aparecerá como variante (ej: Sentadilla → Goblet Squat).</div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-muted block mb-2">Nivel de dificultad</label>
            <div className="flex gap-2 flex-wrap">
              <button type="button" onClick={() => setForm({ ...form, difficulty: "" })}
                className={`text-[10px] uppercase tracking-widest font-bold border px-3 py-2 transition-colors ${
                  !form.difficulty ? "border-[#FF3B30] bg-[#FF3B30] text-white" : "border-[#262626] text-muted hover:text-white"
                }`}
                data-testid="ex-difficulty-none"
              >
                Sin nivel
              </button>
              {DIFFICULTY_OPTIONS.map((d) => (
                <button
                  key={d.key}
                  type="button"
                  onClick={() => setForm({ ...form, difficulty: d.key })}
                  className={`text-[10px] uppercase tracking-widest font-bold border px-3 py-2 transition-colors ${
                    form.difficulty === d.key ? "text-white" : "text-muted hover:text-white"
                  }`}
                  style={form.difficulty === d.key ? { backgroundColor: d.color, borderColor: d.color } : { borderColor: "#262626" }}
                  data-testid={`ex-difficulty-${d.key}`}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div>
        <label className="text-xs uppercase tracking-widest text-muted block mb-2">Instrucciones paso a paso</label>
        <textarea className="input-base" rows="4" placeholder="1. Posición inicial&#10;2. Bajá controlado&#10;3. Empujá desde los talones..." value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} data-testid="ex-instructions" />
      </div>

      <div className="border-t border-app pt-5">
        <div className="text-xs uppercase tracking-widest text-muted mb-3">Tipo de demostración</div>
        <div className="flex flex-wrap gap-2 mb-4">
          {MEDIA_TYPE_OPTIONS.map((o) => {
            const Icon = o.icon;
            return (
              <button
                key={o.key}
                type="button"
                onClick={() => setForm({ ...form, media_type: o.key })}
                className={`flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold border px-3 py-2 transition-colors ${
                  form.media_type === o.key ? "border-[#FF3B30] bg-[#FF3B30] text-white" : "border-[#262626] text-muted hover:text-white"
                }`}
                data-testid={`ex-media-type-${o.key}`}
              >
                <Icon size={14} weight="bold" /> {o.label}
              </button>
            );
          })}
        </div>

        {form.media_type === "youtube" ? (
          <div>
            <label className="text-xs uppercase tracking-widest text-muted block mb-2">URL de YouTube</label>
            <input
              className="input-base"
              placeholder="https://www.youtube.com/watch?v=..."
              value={form.video_url}
              onChange={(e) => onUrlChange("video_url", e.target.value)}
              data-testid="ex-youtube-url"
            />
            <div className="text-[10px] text-muted mt-1">Aceptamos youtube.com/watch, youtu.be, shorts y embeds.</div>
          </div>
        ) : form.media_type === "video" ? (
          <div className="space-y-2">
            <label className="text-xs uppercase tracking-widest text-muted block">Archivo de video (mp4, webm, mov — máx 50MB)</label>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                className="input-base flex-1"
                placeholder="URL del video o subí uno"
                value={form.video_url}
                onChange={(e) => onUrlChange("video_url", e.target.value)}
                data-testid="ex-video-url"
              />
              <label className="btn-secondary cursor-pointer flex items-center gap-2 whitespace-nowrap">
                <UploadSimple size={16} weight="bold" />
                {uploading ? "Subiendo..." : "Subir video"}
                <input type="file" accept="video/*" className="hidden" onChange={(e) => onUpload(e, "video")} data-testid="ex-video-file" />
              </label>
            </div>
            <input
              className="input-base"
              placeholder="(Opcional) URL de imagen de portada (poster)"
              value={form.image_url}
              onChange={(e) => setForm({ ...form, image_url: e.target.value })}
            />
          </div>
        ) : (
          <div className="space-y-2">
            <label className="text-xs uppercase tracking-widest text-muted block">
              {form.media_type === "gif" ? "Archivo GIF (máx 50MB)" : "Imagen (jpg, png, webp)"}
            </label>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                className="input-base flex-1"
                placeholder={form.media_type === "gif" ? "URL del GIF..." : "URL de imagen..."}
                value={form.image_url}
                onChange={(e) => onUrlChange("image_url", e.target.value)}
                data-testid="ex-image-url"
              />
              <label className="btn-secondary cursor-pointer flex items-center gap-2 whitespace-nowrap">
                <UploadSimple size={16} weight="bold" />
                {uploading ? "Subiendo..." : "Subir archivo"}
                <input type="file" accept={form.media_type === "gif" ? "image/gif" : "image/*"} className="hidden" onChange={(e) => onUpload(e, "image")} data-testid="ex-image-file" />
              </label>
            </div>
          </div>
        )}

        {/* Preview */}
        {(form.image_url || form.video_url) && (
          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-widest text-muted mb-2">Preview</div>
            <div className="w-full max-w-xs aspect-square bg-[#0A0A0A] border border-app overflow-hidden">
              <ExerciseMedia exercise={{ image_url: form.image_url, video_url: form.video_url, media_type: form.media_type, name: form.name }} autoHover={false} />
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-3 pt-2">
        <button type="submit" className="btn-primary flex items-center gap-2" data-testid="save-exercise-btn">
          <FloppyDisk size={16} weight="bold" /> {editing ? "Guardar cambios" : "Agregar ejercicio"}
        </button>
        <button type="button" onClick={onCancel} className="btn-secondary">Cancelar</button>
      </div>
    </form>
  );
}

function ExerciseDetailModal({ exercise, allExercises = [], isAdmin, onClose, onOpenVariant, onAddVariant, onEdit }) {
  const variants = allExercises.filter((e) => e.parent_id === exercise.exercise_id);
  const parent = exercise.parent_id ? allExercises.find((e) => e.exercise_id === exercise.parent_id) : null;
  const diffMeta = DIFFICULTY_OPTIONS.find((d) => d.key === exercise.difficulty);
  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={onClose} data-testid="exercise-detail-modal">
      <div className="card-base w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-app flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              {exercise.category && <div className="text-[10px] uppercase tracking-widest text-primary-app font-bold">{exercise.category}</div>}
              {diffMeta && (
                <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 text-white" style={{ backgroundColor: diffMeta.color }} data-testid="difficulty-badge">
                  {diffMeta.label}
                </span>
              )}
              {parent && (
                <button
                  onClick={() => onOpenVariant(parent)}
                  className="text-[9px] uppercase tracking-widest font-bold text-muted hover:text-white border border-[#262626] px-2 py-0.5"
                  data-testid="open-parent"
                >
                  ← Ver madre: {parent.name}
                </button>
              )}
            </div>
            <h2 className="font-display font-black uppercase text-2xl tracking-tighter truncate">{exercise.name}</h2>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {isAdmin && onEdit && (
              <button onClick={onEdit} className="btn-secondary text-xs flex items-center gap-1" data-testid="detail-edit">
                <Pencil size={12} weight="bold" /> Editar
              </button>
            )}
            <button onClick={onClose} className="text-muted hover:text-white" data-testid="close-exercise-detail">
              <X size={22} weight="bold" />
            </button>
          </div>
        </div>
        <div className="aspect-video bg-[#0A0A0A]">
          <ExerciseMedia exercise={exercise} autoHover={false} />
        </div>
        <div className="p-5 space-y-4">
          {Array.isArray(exercise.muscles) && exercise.muscles.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted mb-1">Músculos trabajados</div>
              <div className="flex flex-wrap gap-1">
                {exercise.muscles.map((m) => (
                  <span key={m} className="text-[10px] uppercase tracking-widest font-bold bg-[#262626] text-white px-2 py-1">{m}</span>
                ))}
              </div>
            </div>
          )}
          {exercise.equipment && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted mb-1">Equipamiento</div>
              <div className="text-sm">{exercise.equipment}</div>
            </div>
          )}
          {exercise.description && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted mb-1">Descripción</div>
              <div className="text-sm">{exercise.description}</div>
            </div>
          )}
          {exercise.instructions && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted mb-1">Instrucciones</div>
              <div className="text-sm whitespace-pre-line">{exercise.instructions}</div>
            </div>
          )}
          {exercise.source_url && (
            <div>
              <a
                href={exercise.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-primary-app hover:underline"
                data-testid="source-link"
              >
                Ver demostración original ↗
              </a>
            </div>
          )}

          {/* Variantes */}
          {(variants.length > 0 || (isAdmin && !exercise.parent_id)) && (
            <div className="border-t border-app pt-5">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs uppercase tracking-widest text-muted">
                  Variantes {variants.length > 0 && <span className="text-white">· {variants.length}</span>}
                </div>
                {isAdmin && !exercise.parent_id && onAddVariant && (
                  <button onClick={onAddVariant} className="btn-secondary text-xs flex items-center gap-1" data-testid="add-variant-btn">
                    <Plus size={12} weight="bold" /> Crear variante
                  </button>
                )}
              </div>
              {variants.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="variants-grid">
                  {variants.map((v) => {
                    const vd = DIFFICULTY_OPTIONS.find((d) => d.key === v.difficulty);
                    return (
                      <button
                        key={v.exercise_id}
                        type="button"
                        onClick={() => onOpenVariant(v)}
                        className="card-base !border-app text-left overflow-hidden hover:!border-[#FF3B30] transition-colors"
                        data-testid={`variant-card-${v.exercise_id}`}
                      >
                        <div className="aspect-video bg-[#0A0A0A] relative">
                          <ExerciseMedia exercise={v} autoHover={false} size="sm" />
                          {vd && (
                            <span className="absolute top-1.5 right-1.5 text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 text-white" style={{ backgroundColor: vd.color }}>
                              {vd.label}
                            </span>
                          )}
                        </div>
                        <div className="p-3">
                          <div className="font-display font-bold uppercase text-sm line-clamp-2">{v.name}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="text-[11px] text-muted">
                  Agregá progresiones o regresiones de este ejercicio (ej: Goblet, Búlgara, Jump Squat). Cada variante tiene su propio video y nivel de dificultad.
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
